import * as THREE from "three";
import type { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { PanoramaSource } from "./PanoramaSource.ts";

const PANO_DEFAULT_FOV = 75;
const DRAG_SENSITIVITY = 0.003;
const PITCH_LIMIT = Math.PI * 0.48;

/**
 * Reusable panorama session: BackSide sphere around the camera, drag-to-look,
 * neighbour arrows, WASD/wheel to step. Texture provisioning is delegated to
 * a {@link PanoramaSource}.
 *
 * Visual model: the sphere is a directional skybox parented to the renderer's
 * camera position. World position of the sphere doesn't affect sampling for
 * either the pinhole-blend shader or equirect mapping — sample direction is
 * the only input. So we keep the sphere at the camera each render frame.
 */
export class PanoramaSession {
  private sphere: THREE.Mesh | null = null;
  private active_ = false;
  private loading = false;

  private savedBackground: THREE.Color | THREE.Texture | THREE.CubeTexture | null = null;
  private savedCamPos = new THREE.Vector3();
  private savedCamQuat = new THREE.Quaternion();
  private savedCamFov = 45;
  private savedControlsTarget = new THREE.Vector3();

  private currentNodeId = -1;
  private yaw = 0;
  private pitch = 0;
  private fov = PANO_DEFAULT_FOV;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;

  private overlay: HTMLElement;
  private nav: HTMLElement;
  private info: HTMLElement;
  private loadingDiv: HTMLElement;

  /** Fired after a successful exit (panorama closed, scene restored). */
  onExit: (() => void) | null = null;
  /** Fired after a successful navigate (texture loaded, ready to render). */
  onNodeChange: ((nodeId: number) => void) | null = null;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private controls: OrbitControls,
    private planRoot: THREE.Group,
    private source: PanoramaSource,
    overlayId: string = "pano-overlay",
  ) {
    this.overlay = document.getElementById(overlayId)!;
    this.info = this.overlay.querySelector(".pano-info") as HTMLElement;
    this.nav = this.overlay.querySelector(".pano-nav") as HTMLElement;
    this.loadingDiv = this.overlay.querySelector(".pano-loading") as HTMLElement;

    const c = this.renderer.domElement;
    c.addEventListener("pointerdown", this._down);
    c.addEventListener("pointermove", this._move);
    c.addEventListener("pointerup", this._up);
    c.addEventListener("pointerleave", this._up);
    c.addEventListener("wheel", this._wheel, { passive: false });
  }

  get isActive(): boolean { return this.active_; }
  get nodeId(): number { return this.currentNodeId; }

  /** Find the closest panorama node to the current orbit camera. */
  findNearestToCamera(): number {
    let bestId = -1, bestD2 = Infinity;
    for (const n of this.source.getNodes()) {
      const d2 = n.position.distanceToSquared(this.camera.position);
      if (d2 < bestD2) { bestD2 = d2; bestId = n.id; }
    }
    return bestId;
  }

  /** Enter panorama mode at the given node. Idempotent. */
  async enter(nodeId: number): Promise<void> {
    if (this.active_ || this.loading) return;
    if (!this.source.getNodes().some(n => n.id === nodeId)) return;

    this.active_ = true;
    this._ensureSphere();

    this.savedBackground = this.scene.background as THREE.Color | THREE.Texture | THREE.CubeTexture | null;
    this.savedCamPos.copy(this.camera.position);
    this.savedCamQuat.copy(this.camera.quaternion);
    this.savedCamFov = this.camera.fov;
    this.savedControlsTarget.copy(this.controls.target);

    if (document.pointerLockElement) document.exitPointerLock();
    this.controls.enabled = false;
    this.controls.disconnect();
    this.planRoot.visible = false;
    this.scene.background = new THREE.Color(0x050505);

    this.sphere!.position.copy(this.camera.position);
    this.scene.add(this.sphere!);

    this.fov = PANO_DEFAULT_FOV;
    this._setNodeOrientation(nodeId);
    this._applyCamera();

    this.overlay.classList.add("open");
    this.renderer.domElement.style.cursor = "grab";

    document.addEventListener("keydown", this._key);

    await this.navigate(nodeId);
  }

  /** Leave panorama mode and restore the previous camera/controls state. */
  exit(): void {
    if (!this.active_) return;
    this.active_ = false;

    this.sphere?.removeFromParent();
    this.scene.background = this.savedBackground;
    this.planRoot.visible = true;
    this.controls.connect();
    this.controls.enabled = true;
    this.camera.position.copy(this.savedCamPos);
    this.camera.quaternion.copy(this.savedCamQuat);
    this.camera.fov = this.savedCamFov;
    this.camera.updateProjectionMatrix();
    this.controls.target.copy(this.savedControlsTarget);

    this.overlay.classList.remove("open");
    this.renderer.domElement.style.cursor = "";
    document.removeEventListener("keydown", this._key);

    this.onExit?.();
  }

  /** Switch to another node within an active session. */
  async navigate(nodeId: number): Promise<void> {
    if (!this.active_ || this.loading) return;
    if (!this.source.getNodes().some(n => n.id === nodeId)) return;

    this.currentNodeId = nodeId;
    this._setNodeOrientation(nodeId);
    this._applyCamera();

    this.loading = true;
    this.loadingDiv.style.display = "";

    await this.source.loadNode(nodeId);

    if (!this.active_) { this.loading = false; return; }

    if (this.sphere) this.sphere.rotation.y = this.source.getSphereYaw(nodeId);

    this.loading = false;
    this.loadingDiv.style.display = "none";

    this._renderNav(nodeId);
    const total = this.source.getNodes().length;
    this.info.textContent = `Node ${nodeId} / ${total}`;

    this.source.preloadNeighbors(nodeId);
    this.onNodeChange?.(nodeId);
  }

  /** Step in 2D direction (XZ); picks the most aligned neighbour. */
  navigateInDirection(dx: number, dz: number): void {
    if (this.loading || !this.active_) return;
    const neighbors = this.source.getNeighbors(this.currentNodeId);
    if (!neighbors.length) return;
    const cp = this._pos(this.currentNodeId);
    if (!cp) return;
    const td = new THREE.Vector2(dx, dz).normalize();
    let best = -1, bestDot = -Infinity;
    for (const nid of neighbors) {
      const np = this._pos(nid);
      if (!np) continue;
      const dot = td.dot(new THREE.Vector2(np.x - cp.x, np.z - cp.z).normalize());
      if (dot > bestDot) { bestDot = dot; best = nid; }
    }
    if (best >= 0 && bestDot > 0) this.navigate(best);
  }

  /** Live-update the sphere's Y rotation (used by the alignment UI's yaw slider). */
  setLiveSphereYaw(yaw: number): void {
    if (this.active_ && this.sphere) this.sphere.rotation.y = yaw;
  }

  dispose(): void {
    this.exit();
    if (this.sphere) {
      this.sphere.geometry.dispose();
      this.sphere = null;
    }
    const c = this.renderer.domElement;
    c.removeEventListener("pointerdown", this._down);
    c.removeEventListener("pointermove", this._move);
    c.removeEventListener("pointerup", this._up);
    c.removeEventListener("pointerleave", this._up);
    c.removeEventListener("wheel", this._wheel);
    document.removeEventListener("keydown", this._key);
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private _ensureSphere(): void {
    if (this.sphere) return;
    this.sphere = new THREE.Mesh(
      new THREE.SphereGeometry(50, 64, 32),
      this.source.getMaterial(),
    );
    this.sphere.renderOrder = -1;
  }

  private _setNodeOrientation(nodeId: number): void {
    const node = this.source.getNodes().find(n => n.id === nodeId);
    if (!node) return;
    this.yaw = node.initialYaw;
    this.pitch = node.initialPitch;
  }

  private _pos(id: number): THREE.Vector3 | null {
    return this.source.getNodes().find(n => n.id === id)?.position ?? null;
  }

  private _applyCamera(): void {
    this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch));
    this.camera.rotation.order = "YXZ";
    this.camera.rotation.set(this.pitch, this.yaw, 0);
    this.camera.fov = this.fov;
    this.camera.updateProjectionMatrix();
  }

  private _renderNav(nodeId: number): void {
    this.nav.innerHTML = "";
    const cp = this._pos(nodeId);
    if (!cp) return;
    const sorted = this.source.getNeighbors(nodeId)
      .map(nid => {
        const np = this._pos(nid);
        return np ? { id: nid, angle: Math.atan2(np.x - cp.x, np.z - cp.z) } : null;
      })
      .filter((x): x is { id: number; angle: number } => x !== null)
      .sort((a, b) => a.angle - b.angle);

    for (const { id: nid, angle } of sorted) {
      const btn = document.createElement("button");
      btn.className = "pano-nav-btn";
      const deg = THREE.MathUtils.radToDeg(-angle) + 180;
      btn.innerHTML =
        `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ` +
        `stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" ` +
        `style="transform:rotate(${deg}deg)">` +
        `<line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>`;
      btn.addEventListener("click", () => { void this.navigate(nid); });
      this.nav.appendChild(btn);
    }
  }

  private _down = (e: PointerEvent): void => {
    if (!this.active_ || e.button !== 0) return;
    e.stopPropagation();
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.renderer.domElement.setPointerCapture(e.pointerId);
    this.renderer.domElement.style.cursor = "grabbing";
  };

  private _move = (e: PointerEvent): void => {
    if (!this.active_ || !this.dragging) return;
    e.stopPropagation();
    this.yaw -= (e.clientX - this.lastX) * DRAG_SENSITIVITY;
    this.pitch -= (e.clientY - this.lastY) * DRAG_SENSITIVITY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this._applyCamera();
  };

  private _up = (e: PointerEvent): void => {
    if (!this.active_) return;
    e.stopPropagation();
    this.dragging = false;
    this.renderer.domElement.releasePointerCapture(e.pointerId);
    this.renderer.domElement.style.cursor = "grab";
  };

  private _wheel = (e: WheelEvent): void => {
    if (!this.active_ || this.loading) return;
    e.preventDefault();
    const fwd = e.deltaY < 0 ? 1 : -1;
    this.navigateInDirection(Math.sin(this.yaw) * fwd, Math.cos(this.yaw) * fwd);
  };

  private _key = (e: KeyboardEvent): void => {
    if (!this.active_) return;
    if (e.key === "Escape") { e.stopPropagation(); this.exit(); return; }
    const dirMap: Record<string, [number, number]> = {
      ArrowRight: [1, 0], d: [1, 0], D: [1, 0],
      ArrowLeft: [-1, 0], a: [-1, 0], A: [-1, 0],
      ArrowUp: [0, -1], w: [0, -1], W: [0, -1],
      ArrowDown: [0, 1], s: [0, 1], S: [0, 1],
    };
    const dir = dirMap[e.key];
    if (dir) { e.stopPropagation(); this.navigateInDirection(dir[0], dir[1]); }
  };
}
